'use client';
import {
  ButtonGroup,
  Divider,
  Button as MButton,
  Select,
  TextInput,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';

import { Button, Dropzone } from '@scaleup/frontend/ui';
import { FORM_VALIDATIONS } from '@scaleup/frontend/utils';
import { Certification, certificationTypes } from '@scaleup/models';
import {
  IconArrowLeft,
  IconArrowRight,
  IconAward,
  IconBuilding,
  IconCalendar,
  IconCheck,
  IconCheckbox,
  IconDeviceFloppy,
  IconTag,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useState } from 'react';
import { IntakeBase } from './common';
import { useIntakeUpload, useSupplierIntakeContext } from './context';
import { update, set } from 'lodash';

type Step = 'type' | 'name' | 'verify' | 'manual';
const CertificationHandler = ({
  onFinish,
  setDirty,
}: {
  onFinish: (c: Certification) => void;
  setDirty?: (dirty: boolean) => void;
}) => {
  const { supplier } = useSupplierIntakeContext();
  const [step, setStep] = useState<Step>('type');
  const [certification, setCertification] = useState<Certification>({});

  const updateValue = (key: keyof Certification, value: any) => {
    setCertification({ ...certification, [key]: value });
  };
  const { intakeFileUpload, isLoading } = useIntakeUpload();

  const uploadFile = async (file: File) => {
    await intakeFileUpload(file, (key) => {
      setCertification({
        ...certification,
        fileKey: key,
      });

      onFinish({ ...certification, fileKey: key });
    });
  };

  const form = useForm({
    mode: 'controlled',
    validateInputOnChange: true,
    initialValues: {
      //  section 1
      agency: certification.agency,
      number: certification.number,
      status: certification.status,
      date: certification.date,
    },
    validate: {
      agency: FORM_VALIDATIONS.basicText('Agency', 4, true),
      number: FORM_VALIDATIONS.basicText('Certification Number', 4),
      status: FORM_VALIDATIONS.basicText('Status', 4),
      date: (val?: number) => null,
    },
  });

  const render = () => {
    switch (step) {
      case 'type':
        return (
          <>
            <div
              key="cert_type"
              className="flex flex-col gap-4 w-full min-h[20rem] items-center"
            >
              <div className="w-1/2">
                <Select
                  required
                  label="Certification Type"
                  value={certification.type}
                  classNames={{ input: 'w-full' }}
                  data={Array.from(Object.keys(certificationTypes))}
                  onChange={(val) => {
                    setCertification({
                      ...certification,
                      type: val!,
                      name: undefined,
                    });
                    setStep('name');
                  }}
                />
              </div>
            </div>
          </>
        );
      case 'name':
        return (
          <>
            <div
              key="cert_name"
              className="flex flex-col gap-4 w-full min-h[20rem] items-center"
            >
              <div className="w-1/2">
                <Select
                  disabled={!Boolean(certification.type)}
                  required
                  label="Certification Name"
                  classNames={{ input: 'w-full' }}
                  value={certification.name}
                  data={Array.from(
                    new Set(
                      // @ts-ignore
                      certificationTypes[certification.type!]
                    )
                  )}
                  onChange={(val) => {
                    updateValue('name', val);
                    setStep('manual');
                  }}
                />
              </div>
            </div>
          </>
        );

      case 'verify':
        return (
          <div>
            <div className="flex flex-col gap-4 pb-4">
              <p className="text-text-primary text-center font-bold text-2xl">
                Verify Your Certificate
              </p>
              <p className="text-text-primary text-center">
                Choose how you would like to verify your{' '}
                <strong>{certification.name!}</strong> certificate.
              </p>
            </div>
            <div className="w-full pt-4 flex gap-12">
              {/*<div className="w-full flex flex-col gap-4 flex-1 border-dashed border-primary-500 border rounded-2xl px-4">
                <div className="flex flex-col gap-2 mx-8 mt-8 mb-2">
                  <span className="text-xl font-bold text-center">
                    Upload Certificate
                  </span>
                  <span className="text-base font-normal pt-2 text-center text-nowrap">
                    Upload a copy of your certificate
                  </span>
                </div>
                <div className="pb-14">
                  <Dropzone
                    disabled={Boolean(certification.fileKey)}
                    isLoading={isLoading}
                    displayInstructions
                    handleUpload={uploadFile}
                  />
                  {certification.fileKey && (
                    <div className="flex items-center">
                      <p>
                        File: {certification.fileKey?.split('/')[1]} uploaded
                      </p>
                    </div>
                  )}
                </div>

                <div className="relative h-full w-full">
                  <div className="absolute bottom-0 w-[inherit]"></div>
                </div>
              </div>

              <div className="flex flex-col justify-center">
                <p className="text-3xl font-bold text-primary-600">OR</p>
              </div>*/}

              <div className="w-full flex flex-col gap-4 flex-1 border-dashed border-primary-500 border rounded-2xl px-4">
                <div className="flex flex-col gap-2 my-6 mx-8">
                  <span className="text-xl font-bold text-center">
                    Manually Verify
                  </span>
                  <span className="text-base font-normal pt-2 text-center text-nowrap">
                    Manually enter certification details
                  </span>

                  <div className="pt-16">
                    <Button
                      variant="primary"
                      suffixIcon={<IconArrowRight />}
                      onClick={() => setStep('manual')}
                      text="Continue"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'manual':
        return (
          <div>
            <div className="flex flex-col gap-4 pb-4">
              <p className="text-text-primary text-center font-bold text-2xl">
                Verify Your Certificate
              </p>
              <p className="text-text-primary text-center">
                Please provide the following details for your{' '}
                <strong>{certification.name!}</strong> certificate.
              </p>

              <Divider color="#c6e7ef" />
            </div>

            <div className="flex gap-4 w-full py-4 items-center">
              <div className="w-1/2">
                <p className="text-text-primary font-bold text-sm">
                  Certification
                </p>
                <p className="text-text-primary">{certification.name!}</p>
              </div>
              <div className="w-1/2">
                <TextInput
                  label="Certification Agency"
                  classNames={{ input: 'w-full' }}
                  error={form.errors.agency}
                  {...form.getInputProps('agency')}
                />
              </div>
            </div>

            <div className="flex gap-4 w-full">
              <div className="w-1/3">
                <DateInput
                  label="Date Issued"
                  classNames={{ input: 'w-full' }}
                  valueFormat="MM/YY"
                  maxDate={new Date()}
                  error={form.errors.date}
                  {...form.getInputProps('date')}
                />
              </div>
              <div className="w-1/3">
                <TextInput
                  label="Certification Number"
                  classNames={{ input: 'w-full' }}
                  error={form.errors.number}
                  {...form.getInputProps('number')}
                />
              </div>
              <div className="w-1/3">
                <Select
                  data={['Active', 'Inactive', 'Expired', 'Pending']}
                  label="Certification Status"
                  classNames={{ input: 'w-full' }}
                  error={form.errors.status}
                  {...form.getInputProps('status')}
                />
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <Button
                text="Save Certification"
                variant={!form.isValid() ? 'disabled' : 'secondary'}
                onClick={() => {
                  setCertification({ ...certification, ...form.getValues() });
                  if (setDirty) {
                    setDirty(true);
                  }
                  onFinish({
                    ...certification,
                    ...form.getValues(),
                    date: dayjs(form.getValues().date).unix(),
                  });
                }}
                prefixIcon={<IconDeviceFloppy />}
              />
            </div>
          </div>
        );
    }
  };

  return render();
};

export const Demographics = () => {
  const {
    setCurrentStep,
    demographicCertifications,
    setDemographicCertifications,
    supplier,
    updateSupplier,
    lastStep,
    setLastStep,
  } = useSupplierIntakeContext();
  const [showHandler, setShowHandler] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const handleClick = async () => {
    if (!dirty) {
      if (lastStep === 'review') {
        setLastStep('demographics');
        setCurrentStep('review');
      } else {
        setLastStep('demographics');
        setCurrentStep('industry');
      }
      return;
    }
    // add api call
    const supplierData = {
      ...supplier.supplierData,
      demographicCertifications: demographicCertifications.filter(
        (c) => c.type == 'Demographic Certification'
      ),
      industryCertifications: demographicCertifications.filter(
        (c) => c.type != 'Demographic Certification'
      ),
    };
    setLoading(true);
    updateSupplier({
      ...supplier,
      supplierData: supplierData,
    })
      .then((newSupplier) => {
        setLoading(false);
        console.log('Updated supplier', newSupplier);
        if (lastStep === 'review') {
          setLastStep('demographics');
          setCurrentStep('review');
        } else {
          setLastStep('demographics');
          setCurrentStep('industry');
        }
      })
      .catch(() => {
        setLoading(false);
      });
  };
  const render = () => {
    if (showHandler) {
      return (
        <CertificationHandler
          onFinish={(c) => {
            console.log('C', JSON.stringify(c));
            setDemographicCertifications([...demographicCertifications, c]);
            setShowHandler(false);
          }}
          setDirty={setDirty}
        />
      );
    }

    if (demographicCertifications.length > 0) {
      return (
        <>
          <div className="w-full flex flex-col gap-4 items-center justify-center">
            <div className="flex flex-col gap-4">
              {demographicCertifications.map((cert, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 bg-[#b8e1eb] rounded-[1rem] p-4"
                >
                  <p className="font-bold">{cert.type}</p>
                  <div className="flex items-center gap-4">
                    <div className="w-6 h-6 text-[#4eb3cf] shrink-0">
                      <IconAward />
                    </div>
                    <span
                      className={`text-[#344054] text-base leading-6 $ font-normal`}
                    >
                      {cert.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-6 h-6 text-[#4eb3cf] shrink-0">
                      <IconBuilding />
                    </div>
                    <span
                      className={`text-[#344054] text-base leading-6 $ font-normal`}
                    >
                      {cert.agency}
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-6 h-6 text-[#4eb3cf] shrink-0">
                      <IconCalendar />
                    </div>
                    <span
                      className={`text-[#344054] text-base leading-6 $ font-normal`}
                    >
                      {cert.date
                        ? dayjs(cert.date * 1000).format('MM/YYYY')
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-6 h-6 text-[#4eb3cf] shrink-0">
                      <IconTag />
                    </div>
                    <span
                      className={`text-[#344054] text-base leading-6 $ font-normal`}
                    >
                      {cert.number || 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-6 h-6 text-[#4eb3cf] shrink-0">
                      <IconCheckbox />
                    </div>
                    <span
                      className={`text-[#344054] text-base leading-6 $ font-normal`}
                    >
                      Status: {cert.status || 'N/A'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <div className="py-2 w-full">
              <Divider className="w-full" color="#b8e1eb" />
            </div>

            <p className="text-text-primary text-xl">
              Would you like to add another Certification?
            </p>
            <ButtonGroup className="text-primary-500">
              <MButton
                onClick={() => setShowHandler(true)}
                variant="outline"
                color="#5fb9d3"
              >
                Yes
              </MButton>
              <MButton
                onClick={() => {
                  if (lastStep === 'review') {
                    setLastStep('demographics');
                    setCurrentStep('review');
                  } else {
                    setLastStep('demographics');
                    setCurrentStep('industry');
                  }
                }}
                variant="outline"
                color="#5fb9d3"
              >
                No
              </MButton>
            </ButtonGroup>
          </div>
        </>
      );
    }

    return (
      <>
        <div>
          <p>Has the company obtained any certifications?</p>
        </div>
        <ButtonGroup className="text-primary-500">
          <MButton
            onClick={() => setShowHandler(true)}
            variant="outline"
            color="#5fb9d3"
          >
            Yes
          </MButton>
          <MButton
            onClick={() => {
              if (lastStep === 'review') {
                setLastStep('demographics');
                setCurrentStep('review');
              } else {
                setLastStep('demographics');
                setCurrentStep('industry');
              }
            }}
            variant="outline"
            color="#5fb9d3"
          >
            No
          </MButton>
        </ButtonGroup>
      </>
    );
  };

  return (
    <IntakeBase currentStep={1}>
      <div className="flex flex-col gap-2">
        <p className="text-text-primary font-bold text-xl">
          Certifications & Compliance
        </p>
      </div>

      <div className="rounded-[1rem] bg-primary-25 p-8 my-8 w-full min-h-[20rem] flex flex-col gap-4 items-center justify-center">
        {render()}
      </div>

      <div className="flex justify-between">
        <Button
          text="Back"
          variant="secondary"
          prefixIcon={<IconArrowLeft />}
          onClick={() => {
            if (lastStep === 'review') {
              setLastStep('demographics');
              setCurrentStep('review');
            } else {
              setLastStep('demographics');
              setCurrentStep('details');
            }
          }}
        />

        <Button
          text="Continue"
          variant="primary"
          suffixIcon={<IconArrowRight />}
          onClick={handleClick}
          loading={loading}
        />
      </div>
    </IntakeBase>
  );
};
